import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PAYMENT_METHOD_LABELS, type SaleRowDTO } from '@bifeai/shared';
import { Badge, Card, CardBody, EmptyState, Input, PageHeader, Skeleton } from '@bifeai/ui';
import { Receipt } from 'lucide-react';
import { useState } from 'react';
import { SaleDetailDialog } from '../components/SaleDetailDialog';
import { SearchBox, Toolbar } from '../components/inventory';
import { api } from '../lib/api';
import { formatDateTime, formatMoney } from '../lib/format';
import { useSession } from '../lib/session';
import { todayIso } from '../lib/units';

export function SalesPage() {
  const { multiBranch, selectedBranchId } = useSession();
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [status, setStatus] = useState<'' | 'COMPLETED' | 'VOIDED'>('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  if (status) qs.set('status', status);
  if (search.trim()) qs.set('search', search.trim());
  const sales = useQuery({
    queryKey: ['sales', 'list', { from, to, status, search }],
    queryFn: () => api.get<SaleRowDTO[]>(`/sales?${qs.toString()}`),
    placeholderData: keepPreviousData,
  });
  const rows = sales.data ?? [];
  const completed = rows.filter((r) => r.status === 'COMPLETED');
  const total = completed.reduce((a, r) => a + r.totalCents, 0);

  return (
    <>
      <PageHeader
        title="Ventas"
        subtitle={
          rows.length > 0
            ? `${completed.length} ${completed.length === 1 ? 'venta' : 'ventas'} por ${formatMoney(total)} en el período.`
            : 'Todos los tickets: reimprimí, revisá o anulá una venta.'
        }
      />
      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Buscar por número de ticket" />
        <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Desde
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 160 }} />
        </label>
        <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Hasta
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 160 }} />
        </label>
        <div className="bf-chips" role="group" aria-label="Estado">
          <button type="button" className="bf-chip" aria-pressed={status === ''} onClick={() => setStatus('')}>
            Todas
          </button>
          <button type="button" className="bf-chip" aria-pressed={status === 'VOIDED'} onClick={() => setStatus('VOIDED')}>
            Anuladas
          </button>
        </div>
      </Toolbar>
      <Card>
        <CardBody flush>
          {sales.isPending ? (
            <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={<Receipt />} title="No hay ventas en este período" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Fecha</th>
                    {multiBranch && !selectedBranchId && <th>Sucursal</th>}
                    <th>Atendió</th>
                    <th>Pago</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-clickable="true" onClick={() => setOpen(r.id)}>
                      <td>
                        <span className="bf-cell-main bf-num">{r.ticket}</span> {r.status === 'VOIDED' && <Badge tone="danger">Anulada</Badge>}
                        {r.refundPending && <Badge tone="warning">Devolución pendiente</Badge>}
                        {r.offline && <Badge>Sin internet</Badge>}
                        {r.fiscal && r.fiscal.status !== 'NONE' && (
                          <Badge tone={r.fiscal.status === 'AUTHORIZED' ? 'success' : r.fiscal.status === 'PENDING' ? 'warning' : 'danger'}>
                            {r.fiscal.status === 'AUTHORIZED'
                              ? `Factura ${r.fiscal.letter}`
                              : r.fiscal.status === 'PENDING'
                                ? 'Factura pendiente'
                                : 'Factura rechazada'}
                          </Badge>
                        )}
                        {r.fiscal?.number && <span className="bf-cell-sub"> {r.fiscal.number}</span>}
                        <div className="bf-cell-sub">
                          {r.items} {r.items === 1 ? 'producto' : 'productos'}
                        </div>
                      </td>
                      <td className="bf-cell-sub">{formatDateTime(r.createdAt)}</td>
                      {multiBranch && !selectedBranchId && <td>{r.branchName}</td>}
                      <td>{r.cashierName}</td>
                      <td className="bf-cell-sub">
                        {r.methods.map((m) => PAYMENT_METHOD_LABELS[m]).join(' + ')}
                        {r.customerName && <div>{r.customerName}</div>}
                      </td>
                      <td
                        className={`bf-num ${r.status === 'VOIDED' ? 'bf-text-muted' : ''}`}
                        style={{
                          textAlign: 'right',
                          fontWeight: 600,
                          textDecoration: r.status === 'VOIDED' ? 'line-through' : undefined,
                        }}
                      >
                        {formatMoney(r.totalCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {open && <SaleDetailDialog saleId={open} onClose={() => setOpen(null)} />}
    </>
  );
}
